import React, { useMemo } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const AvatarScoreboardWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const scoreboard = useMemo(() => {
    const sessionAvatars = state?.sessionAvatars ?? [];
    const pendingContributions = state?.pendingContributions ?? [];
    const counts = new Map<string, number>();
    for (const c of pendingContributions) {
      counts.set(c.role, (counts.get(c.role) || 0) + 1);
    }

    return sessionAvatars
      .map(avatar => ({
        ...avatar,
        count: counts.get(avatar.role) || 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [state?.sessionAvatars, state?.pendingContributions]);

  if (!state) return null;

  const maxCount = Math.max(1, ...scoreboard.map(a => a.count));

  return (
    <div className="flex flex-col h-full px-3 py-2 gap-1.5 overflow-y-auto">
      {scoreboard.map((avatar, index) => {
        let rankClass: string;
        if (index === 0 && avatar.count > 0) rankClass = 'text-primary';
        else rankClass = isDark ? 'text-white/30' : 'text-slate-400';

        return (
        <div key={avatar.role} className="flex items-center gap-2.5 shrink-0">
          {/* Rank */}
          <span className={`text-xs font-bold w-4 text-right ${rankClass}`}>
            {index + 1}
          </span>

          {/* Avatar */}
          <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 border border-white/10">
            {avatar.imageUrl ? (
              <img src={avatar.imageUrl} alt={avatar.name} className="w-full h-full object-cover" />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: avatar.primaryColor || '#475569', color: '#fff' }}
              >
                {avatar.emoji || avatar.name.charAt(0)}
              </div>
            )}
          </div>

          {/* Name */}
          <span className={`text-xs font-medium w-20 truncate ${
            isDark ? 'text-white/70' : 'text-slate-600'
          }`}>
            {avatar.name}
          </span>

          {/* Bar */}
          <div className={`flex-1 h-4 rounded-full overflow-hidden ${
            isDark ? 'bg-white/5' : 'bg-slate-100'
          }`}>
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary/80 to-primary transition-all duration-700 ease-out"
              style={{ width: `${(avatar.count / maxCount) * 100}%` }}
            />
          </div>

          {/* Count */}
          <span className={`text-sm font-bold font-mono w-6 text-right ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            {avatar.count}
          </span>
        </div>
        );
      })}
    </div>
  );
};

export default AvatarScoreboardWidget;
